import { Button, Modal, ModalActions, modalClasses, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { FormProvider, useForm } from "react-hook-form";
import { useId, useState } from "react";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { CreateDraftEntryForm } from "./form/CreateDraftEntryForm";
import styled from "@emotion/styled";

export interface CreateDraftEntryModalProps {
    /** is the modal currently open */
    open: boolean;

    /** cancel button callback */
    onCancel: () => void;

    /** form submit url */
    submitUrl: string;

    /** tags autocomplete search url */
    tagsSearchUrl: string;
}

const StyledModal = styled(Modal)({
    [`& .${modalClasses.paper}`]: {
        maxWidth: 664,
    },
});

/**
 * Modal for creating a new draft media entry; manages form state, submission, and analytics.
 */
export function CreateDraftEntryModal(props: CreateDraftEntryModalProps) {
    const { open, onCancel, submitUrl, tagsSearchUrl } = props;

    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();

    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            description: "",
            tags: "",
        },
    });

    const sendButtonAnalytics = useButtonAnalytics();

    const handleSubmit = async (data: { [key: string]: string | boolean }) => {
        setProcessing(true);

        try {
            const result = await postKmsData(`${baseUrl}${submitUrl}`, data);
            if (result.success && result.redirectUrl) {
                showToast({ severity: "success", message: translate("Draft entry successfully created") });
                sendButtonAnalytics(
                    "Draft entry - create",
                    ButtonClickAnalyticsType.CREATE,
                    result.entryId,
                    "DraftEntry",
                    "Modal - Create draft entry"
                );
                setProcessing(false);
                window.location.href = result.redirectUrl;
                onCancel();
            }
            if (!result.success) {
                setProcessing(false);
                if (result.field && result.field === "name") {
                    useFormHookMethods.setError("name", { type: "manual", message: result.errorMessage });
                }
                else {
                    showToast({ severity: "error", message: result.errorMessage });
                }
            }
        }
        catch (e) {
            setProcessing(false);
            showToast({ severity: "error", message: translate("Failed to create draft entry") });
        }
    };

    const handleCancel = () => {
        sendButtonAnalytics(
            "Draft entry - cancel",
            ButtonClickAnalyticsType.CLOSE,
            undefined,
            "DraftEntry",
            "Modal - Create draft entry"
        );
        onCancel();
    };

    const baseClassName = "kms-ds-create-draft-entry-modal";
    const formId = useId();

    return (
        <StyledModal open={open} onClose={handleCancel} fullWidth classes={{ paper: `${baseClassName}` }}>
            <ModalTitle className={`${baseClassName}-title`}>{translate("Create draft entry")}</ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                <FormProvider {...useFormHookMethods}>
                    {/* the Modal component keeps the form mounted in the background even when closed, so the "open" is what forces a remount/reset on each open */}
                    {open && (
                        <CreateDraftEntryForm formId={formId} onSubmit={handleSubmit} tagsSearchUrl={tagsSearchUrl} />
                    )}
                </FormProvider>
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleCancel}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    form={formId}
                    type={"submit"}
                    loading={processing}
                    disabled={processing || !useFormHookMethods.formState.isValid}
                    className={`${baseClassName}-actions-submit`}
                >
                    {translate("Create")}
                </Button>
            </ModalActions>
        </StyledModal>
    );
}

export default CreateDraftEntryModal;
